import useAxiosInterceptor from 'src/services/interceptor';
import { physician } from '../constants';

// these come from the front-ent (document-management-constants.ts)
export type DocumentStatus = 'AWAITING SIGNATURE' | 'UPCOMING' | 'URGENT' | 'OVERDUE' | 'COMPLETED';
// these come from the API server (SignatureStatus.java)
export type DocumentSignatureStatus = 'PENDING' | 'COMPLETE';
// this is a set made for the supervisor signature workflow
export type DocumentSignatureWorkflowStatuses = 'PENDING_APPROVAL' | 'APPROVED' | 'SIGNED';
// this is a composite set for use displaying as fulls tatus information as possible
export type DocumentSignatureCompositeStatuses =
  | ''
  | DocumentStatus
  | DocumentSignatureWorkflowStatuses;

export function compositeWorkflowStatusFromDocument(
  document: PhysicianDocument
): DocumentSignatureCompositeStatuses {
  if (!document) return '';

  if (document.supervisorStatus === 'COMPLETE') {
    return 'SIGNED';
  } else if (document.supervisorStatus === 'PENDING' && document.approvedForSupervisorSignature) {
    return 'APPROVED';
  } else {
    if (document.status === 'COMPLETED') {
      return 'PENDING_APPROVAL';
    } else if (document.status === 'OVERDUE') {
      return 'OVERDUE';
    } else if (document.status === 'URGENT') {
      return 'URGENT';
    } else if (document.status === 'UPCOMING') {
      return 'UPCOMING';
    } else if (document.status === 'AWAITING SIGNATURE') {
      return 'AWAITING SIGNATURE';
    } else {
      return '';
    }
  }
}

export interface PhysicianDTO {
  patientIds: Array<string> | [];
  facilityIds: Array<string> | [];
  type: Array<string> | [];
  startDateOfService: string | null;
  endDateOfService: string | null;
  startDueDate: string | null;
  endDueDate: string | null;
  status: string | null;
  sortBy: 'facilityName' | 'patientName' | 'documentType' | 'dateOfService' | 'dueDate';
  sortDirection: 'asc' | 'desc';
  page: number;
  pageSize: number;
}

export interface PhysicianDocument {
  uuid: string;
  providerId: string | null;
  formId: string | null;
  submissionId: string | null;
  episodeOfCareId: string | null;
  type: string | null;
  dueDate: string | null;
  lastUpdatedOn: string | null;
  status: string;
  supervisorStatus: string | null;
  documentType: string | null;
  dueDateToSign: string | null;
  signees: string | null;
  signedBy: string | null;
  pdfUrl: string | null;
  isForSupervisor: string | null;
  supervisorId: string | null;
  supervisorName: string | null;
  patientName: string | null;
  lastUpdatedOnString: string | null;
  dueDateString: string | null;
  forSign: string | null;
  mailSentToPhysician: string | null;
  formType: string | null;
  patientId: string | null;
  facilityName: string | null;
  prefillUrl: string | null;
  dailySessionNoteUuid: string | null;
  isReassigned: string | null;
  dateOfService: string | null;
  visits: string | null;
  payerName: string | null;
  sectionGg: string | null;
  documentKey: string | null;
  rawDataS3Url: string | null;
  facilityId: string | null;
  documentUrl: string | null;
  reformId: string | null;
  approvedForSignature: boolean | null;
  approvedForSupervisorSignature: boolean | null;
}

export const initialPhysicianDTO: PhysicianDTO = {
  patientIds: [],
  facilityIds: [],
  type: [],
  startDateOfService: null,
  endDateOfService: null,
  startDueDate: null,
  endDueDate: null,
  status: null,
  sortBy: 'facilityName',
  sortDirection: 'desc',
  page: 0,
  pageSize: 20,
};

class PhysicianService {
  private axiosInstance;

  constructor() {
    this.axiosInstance = useAxiosInterceptor();
  }

  getAllPhysicianDocuments = (physicianId: string, physicianDto: PhysicianDTO) => {
    return this.axiosInstance.post(physician.getAllDocumentsOfPhysician(physicianId), physicianDto);
  };

  getAllFacility = (payload: any) => {
    return this.axiosInstance.get(`${physician?.facilities}?active=true`, {
      params: {
        ...payload,
      },
    });
  };

  checkPhysicianSign = (payload: any) => {
    return this.axiosInstance.put(`/provider/sync-signature/${payload?.formsibmissionId}`, null, {
      params: {
        facilityIdFromParam: payload?.facilityIdFromParam,
      },
    });
  };

  getAllPatient = (payload: any) => {
    return this.axiosInstance.get(`${physician?.getAllPatient}`, {
      params: {
        ...payload,
      },
    });
  };

  signPhysicianDocument = (payload: any) => {
    return this.axiosInstance.post(`/provider/signature/sign-document`, null, {
      params: {
        ...payload,
      },
      headers: {
        'X-Forwarded-For': localStorage.getItem('ip-address') ?? '',
      },
    });
  };

  bulkSignPhysicianDocuments = (physicianId: string, documentIds: string[], signatureStatus: string, signerName: string, consentConfirmation: boolean = false) => {
    return this.axiosInstance.post(`/provider/signature/bulk-signature/physician/${physicianId}`, null, {
      params: {
        documentIds: documentIds,
        signatureStatus,
        signerName,
        consentConfirmation
      },
      headers: {
        'X-Forwarded-For': localStorage.getItem('ip-address') ?? '',
      },
      paramsSerializer: params => {
        // Create URL-encoded string with [] notation for arrays
        const parts = [];
        for (const key in params) {
          if (Array.isArray(params[key])) {
            params[key].forEach((val: any) => {
              parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(val)}`);
            });
          } else {
            parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`);
          }
        }
        return parts.join('&');
      }
    });
  };

  updateDocumentStatuses = (
    physicianId: string,
    physicianDocumentUuids: PhysicianDocument['uuid'][],
    physicianSignatureStatus: DocumentSignatureWorkflowStatuses,
    physicianSignature: string | null
  ) => {
    // Use the new bulk signature endpoint when signing documents
    if (physicianSignatureStatus === 'SIGNED') {
      return this.bulkSignPhysicianDocuments(
        physicianId,
        physicianDocumentUuids,
        physicianSignatureStatus,
        physicianSignature || '',
        true
      );
    }

    // Use the original endpoint for other status updates (like APPROVED)
    return this.axiosInstance.put(physician.updatePhysicianDocumentStatuses(physicianId), null, {
      params: {
        documentIds: physicianDocumentUuids.join(','),
        signatureStatus: physicianSignatureStatus,
        signatureName: physicianSignature,
      },
    });
  };
}

const physicianService = new PhysicianService();

Object.freeze(physicianService);

export default physicianService;
